import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { ChatView, MessageReactionView, MessageView, PollView } from '@olmt/shared'
import { expireMessages } from '../lib/chat'

export type Presence = { online: boolean; lastSeenAt: number | null }

interface ChatState {
  chats: Record<string, ChatView>
  /** Chronological (oldest first), matching `MessagesResponse.messages`. */
  messages: Record<string, MessageView[]>
  hasMore: Record<string, boolean>
  loaded: Record<string, boolean>
  /** chatId -> userId -> epoch ms until which the indicator is valid. */
  typing: Record<string, Record<string, number>>
  presence: Record<string, Presence>

  setChats: (chats: ChatView[]) => void
  upsertChat: (chat: ChatView) => void
  /** Drops a chat from the list and every per-chat map — used after leaving a group. */
  removeChat: (chatId: string) => void
  patchChat: (chatId: string, patch: Partial<ChatView>) => void
  setMessages: (chatId: string, messages: MessageView[], hasMore: boolean) => void
  prependMessages: (chatId: string, messages: MessageView[], hasMore: boolean) => void
  upsertMessage: (message: MessageView) => void
  /**
   * Replace a stored message with the view a `message.edited` frame or a successful `PATCH`
   * carries. A message the client does not hold is left alone: the frozen protocol says the frame
   * is ignored there, never appended (the snapshot any later page comes from already reflects it).
   */
  applyEdited: (message: MessageView) => void
  /**
   * Mark a stored message deleted (W2). The body stays where it is — the tombstone is `deletedAt`
   * alone; the bubble hides the text. Same "ignore what we do not hold" rule as `applyEdited`.
   */
  applyDeleted: (chatId: string, messageId: string) => void
  /**
   * Replace one stored message's `reactions` with the list a `message.reaction` frame (W9) or a
   * successful reaction call carries. The list travels whole on purpose, so it is applied whole —
   * never merged, which would need ordering rules of its own. Same "ignore what we do not hold"
   * rule as `applyEdited`.
   */
  applyReactions: (chatId: string, messageId: string, reactions: MessageReactionView[]) => void
  /**
   * Replace one stored message's `poll` with the view a `message.poll` frame (W11) or a successful
   * vote carries. The whole view travels on purpose, so it is applied whole — never merged, which
   * would need ordering rules of its own. Same "ignore what we do not hold" rule as `applyEdited`.
   */
  applyPoll: (chatId: string, messageId: string, poll: PollView) => void
  /**
   * W13 — a `messages.expired` batch: those messages passed their chat's timer and are **gone**
   * (the frame names ids; there is no tombstone to mark), so the rows are dropped rather than
   * flagged, and the chat row's preview and pinned bar move off any message the batch took. Same
   * "ignore what we do not hold" rule as `applyEdited`.
   */
  applyExpired: (chatId: string, messageIds: string[]) => void
  removeMessage: (chatId: string, messageId: string) => void
  bumpUnread: (chatId: string, at: number) => void
  clearUnread: (chatId: string, lastReadId: string | null) => void
  /**
   * Moves one member's read cursor forward in `ChatView.reads` (A15). Live `read` frames are the
   * only source after the initial REST seed; the cursor never moves backwards.
   */
  advancePeerRead: (chatId: string, userId: string, messageId: string) => void
  setTyping: (chatId: string, userId: string, until: number) => void
  setPresence: (userId: string, presence: Presence) => void
  /**
   * A search hit's jump (W3): the pane that shows the result asks for it, and the thread that owns
   * the history and the scroller performs it — one implementation, and the notice about a message
   * older than what is loaded lands where the reader ends up.
   */
  pendingJump: { chatId: string; messageId: string } | null
  requestJump: (chatId: string, messageId: string) => void
  /** Taken by the thread once it has performed the pending jump; never by the pane that asked. */
  clearJump: () => void
  /** The message a completed jump landed on; its bubble wears a brass ring until this clears. */
  highlightId: string | null
  setHighlight: (messageId: string | null) => void
  reset: () => void
}

const dedupe = (list: MessageView[]): MessageView[] => {
  const seen = new Set<string>()
  const out: MessageView[] = []
  for (const message of list) {
    if (seen.has(message.id)) continue
    seen.add(message.id)
    out.push(message)
  }
  return out
}

const EMPTY: Omit<
  ChatState,
  | 'setChats'
  | 'upsertChat'
  | 'removeChat'
  | 'patchChat'
  | 'setMessages'
  | 'prependMessages'
  | 'upsertMessage'
  | 'applyEdited'
  | 'applyDeleted'
  | 'applyReactions'
  | 'applyPoll'
  | 'applyExpired'
  | 'removeMessage'
  | 'bumpUnread'
  | 'clearUnread'
  | 'advancePeerRead'
  | 'setTyping'
  | 'setPresence'
  | 'requestJump'
  | 'clearJump'
  | 'setHighlight'
  | 'reset'
> = {
  chats: {},
  messages: {},
  hasMore: {},
  loaded: {},
  typing: {},
  presence: {},
  pendingJump: null,
  highlightId: null,
}

export const useChatStore = create<ChatState>()(
  subscribeWithSelector((set) => ({
    ...EMPTY,

    setChats: (chats) =>
      set((state) => {
        const next: Record<string, ChatView> = {}
        for (const chat of chats) next[chat.id] = chat
        // Keep locally-known chats that the server did not return (e.g. just created).
        for (const [id, chat] of Object.entries(state.chats)) if (!next[id]) next[id] = chat
        return { chats: next }
      }),

    upsertChat: (chat) => set((state) => ({ chats: { ...state.chats, [chat.id]: chat } })),

    removeChat: (chatId) =>
      set((state) => {
        const chats = { ...state.chats }
        delete chats[chatId]
        const messages = { ...state.messages }
        delete messages[chatId]
        const hasMore = { ...state.hasMore }
        delete hasMore[chatId]
        const loaded = { ...state.loaded }
        delete loaded[chatId]
        const typing = { ...state.typing }
        delete typing[chatId]
        return { chats, messages, hasMore, loaded, typing }
      }),

    patchChat: (chatId, patch) =>
      set((state) => {
        const existing = state.chats[chatId]
        if (!existing) return state
        return { chats: { ...state.chats, [chatId]: { ...existing, ...patch } } }
      }),

    setMessages: (chatId, messages, hasMore) =>
      set((state) => ({
        messages: { ...state.messages, [chatId]: dedupe(messages) },
        hasMore: { ...state.hasMore, [chatId]: hasMore },
        loaded: { ...state.loaded, [chatId]: true },
      })),

    prependMessages: (chatId, messages, hasMore) =>
      set((state) => {
        const existing = state.messages[chatId] ?? []
        const known = new Set(existing.map((message) => message.id))
        const older = messages.filter((message) => !known.has(message.id))
        return {
          messages: { ...state.messages, [chatId]: [...older, ...existing] },
          hasMore: { ...state.hasMore, [chatId]: hasMore },
        }
      }),

    // Reconciles the REST response, the WS broadcast and the optimistic copy by clientId
    // (docs/CONTRACT.md §5).
    upsertMessage: (message) =>
      set((state) => {
        const list = state.messages[message.chatId] ?? []
        const index = list.findIndex(
          (m) =>
            m.id === message.id ||
            (message.clientId !== undefined && m.clientId !== undefined && m.clientId === message.clientId),
        )
        let next: MessageView[]
        if (index === -1) {
          next = [...list, message]
        } else {
          next = list.slice()
          next[index] = { ...message, pending: false }
        }
        const chats = { ...state.chats }
        const chat = chats[message.chatId]
        if (chat) {
          const isNewest = chat.lastMessageAt === null || message.createdAt >= chat.lastMessageAt
          if (isNewest) chats[message.chatId] = { ...chat, lastMessage: message, lastMessageAt: message.createdAt }
        }
        return { messages: { ...state.messages, [message.chatId]: next }, chats }
      }),

    applyEdited: (message) =>
      set((state) => {
        const list = state.messages[message.chatId]
        const index = list?.findIndex((m) => m.id === message.id) ?? -1
        if (!list || index === -1) return state
        const next = list.slice()
        next[index] = { ...message, pending: false }
        const chats = { ...state.chats }
        const chat = chats[message.chatId]
        if (chat) {
          // The list row's preview is `lastMessage`, so an edit to the newest row must land there
          // too — a reload would show the new body from the server, and the live list must not lag.
          let updated = chat
          if (chat.lastMessage?.id === message.id) updated = { ...updated, lastMessage: next[index] }
          // The pinned bar prints the same message's words (W5), so an edit to the chat's pinned
          // message must land there too — the same rule, one row lower.
          if (chat.pinnedMessage?.id === message.id) updated = { ...updated, pinnedMessage: next[index] }
          if (updated !== chat) chats[message.chatId] = updated
        }
        return { messages: { ...state.messages, [message.chatId]: next }, chats }
      }),

    applyDeleted: (chatId, messageId) =>
      set((state) => {
        const list = state.messages[chatId]
        const index = list?.findIndex((m) => m.id === messageId) ?? -1
        const chat = state.chats[chatId]
        // The api clears a pin whose message is deleted and broadcasts the bar coming down in the
        // same breath; doing the same here keeps the bar from showing a tombstone while the socket
        // is down — or between the delete and the frame.
        const clearsPin = chat?.pinnedMessage?.id === messageId
        if (index === -1 && !clearsPin) return state

        let messages = state.messages
        let chats = state.chats
        if (list && index !== -1) {
          const next = list.slice()
          // Keep the first stamp: the frame and the deleter's own REST response arrive in either
          // order, and re-stamping would move a tombstone's time for no reason.
          next[index] = { ...list[index], deletedAt: list[index].deletedAt ?? Date.now(), pending: false }
          messages = { ...messages, [chatId]: next }
          // The list row's preview is `lastMessage`, so a delete to the newest row must land there
          // too — a reload would show the tombstone from the server, and the live list must not lag.
          if (chat?.lastMessage?.id === messageId) {
            chats = { ...chats, [chatId]: { ...chat, lastMessage: next[index] } }
          }
        }
        if (clearsPin) {
          const known = chats[chatId]
          if (known) chats = { ...chats, [chatId]: { ...known, pinnedMessage: null } }
        }
        return { messages, chats }
      }),

    applyReactions: (chatId, messageId, reactions) =>
      set((state) => {
        const list = state.messages[chatId]
        const index = list?.findIndex((m) => m.id === messageId) ?? -1
        if (!list || index === -1) return state
        const next = list.slice()
        next[index] = { ...list[index], reactions }
        // Only the thread's own list changes: the chat-list preview and the pinned bar print the
        // message's words, never its chips, so neither copy of the row needs the new list.
        return { messages: { ...state.messages, [chatId]: next } }
      }),

    applyPoll: (chatId, messageId, poll) =>
      set((state) => {
        const list = state.messages[chatId]
        const index = list?.findIndex((m) => m.id === messageId) ?? -1
        if (!list || index === -1) return state
        const next = list.slice()
        next[index] = { ...list[index], poll }
        // Same as `applyReactions`: the preview and the pinned bar show the question, never the
        // tallies, so there is nothing else to update.
        return { messages: { ...state.messages, [chatId]: next } }
      }),

    /**
     * W13 — the sweep's batch, applied as the frame words it: the ids are gone, so the rows go with
     * them. This is the one frame that removes rather than marks, because the api's `messages.expired`
     * reports a hard delete (§4, §8) — the tombstone `message.deleted` leaves would be a lie here.
     *
     * The chat row's `lastMessage` is its preview and the list's sort key, so when the batch took the
     * message standing there the row moves to the newest survivor, and its stamp moves with it —
     * which is what the api's own `chatView` recomputes on the next load. With nothing loaded and
     * nothing left the stamp stays as it was, again matching the api (the sweep deletes rows, it does
     * not rewrite `chats.last_message_at`). A pinned message that expired leaves no bar: a bar
     * pointing at a message that no longer exists is worse than no bar (docs/DESIGN.md §18), and the
     * api's view drops it for the same reason — it just has no frame to say so with.
     */
    applyExpired: (chatId, messageIds) =>
      set((state) => {
        const gone = new Set(messageIds)
        const chat = state.chats[chatId]
        const expired = expireMessages(state.messages[chatId] ?? [], gone)
        const takesLast = chat?.lastMessage != null && gone.has(chat.lastMessage.id)
        const clearsPin = chat?.pinnedMessage != null && gone.has(chat.pinnedMessage.id)
        if (!expired.changed && !takesLast && !clearsPin) return state

        const messages = expired.changed
          ? { ...state.messages, [chatId]: expired.messages }
          : state.messages
        let chats = state.chats
        if (chat && (takesLast || clearsPin)) {
          const next = { ...chat }
          if (takesLast) {
            next.lastMessage = expired.newest
            if (expired.newest) next.lastMessageAt = expired.newest.createdAt
          }
          if (clearsPin) next.pinnedMessage = null
          chats = { ...chats, [chatId]: next }
        }
        return { messages, chats }
      }),

    removeMessage: (chatId, messageId) =>
      set((state) => ({
        messages: {
          ...state.messages,
          [chatId]: (state.messages[chatId] ?? []).filter((message) => message.id !== messageId),
        },
      })),

    bumpUnread: (chatId, at) =>
      set((state) => {
        const chat = state.chats[chatId]
        if (!chat) return state
        return {
          chats: {
            ...state.chats,
            [chatId]: { ...chat, unreadCount: chat.unreadCount + 1, lastMessageAt: at },
          },
        }
      }),

    clearUnread: (chatId, lastReadId) =>
      set((state) => {
        const chat = state.chats[chatId]
        if (!chat) return state
        return { chats: { ...state.chats, [chatId]: { ...chat, unreadCount: 0, lastReadId } } }
      }),

    advancePeerRead: (chatId, userId, messageId) =>
      set((state) => {
        const chat = state.chats[chatId]
        if (!chat) return state
        const known = chat.reads.find((read) => read.userId === userId)
        if (known && known.messageId >= messageId) return state
        const reads = known
          ? chat.reads.map((read) => (read.userId === userId ? { userId, messageId } : read))
          : [...chat.reads, { userId, messageId }]
        return { chats: { ...state.chats, [chatId]: { ...chat, reads } } }
      }),

    setTyping: (chatId, userId, until) =>
      set((state) => {
        const chat = state.typing[chatId] ?? {}
        const now = Date.now()
        const next: Record<string, number> = {}
        for (const [id, value] of Object.entries(chat)) if (value > now) next[id] = value
        if (until > now) next[userId] = until
        return { typing: { ...state.typing, [chatId]: next } }
      }),

    setPresence: (userId, presence) =>
      set((state) => ({ presence: { ...state.presence, [userId]: presence } })),

    requestJump: (chatId, messageId) => set({ pendingJump: { chatId, messageId }, highlightId: null }),

    clearJump: () => set({ pendingJump: null }),

    setHighlight: (messageId) => set({ highlightId: messageId }),

    reset: () => set({ ...EMPTY }),
  })),
)
